import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

/**
 * M21 L21.07 — Regulator export governance.
 *
 * Lists regulator export requests from `GET /v1/audit/regulator-exports`,
 * allows requesting a new export (`POST`) and driving its lifecycle
 * (`POST :id/generate | :id/deliver | :id/cancel`). Generation compiles a
 * checksummed extract of the compliance audit trail server-side.
 */
interface RegulatorExportRow {
  readonly regulatorExportId: string;
  readonly regulatorName: string;
  readonly inquiryReference?: string | null;
  readonly scope: string;
  readonly exportFormat: string;
  readonly status: string;
  readonly recordCount?: number | null;
  readonly checksumSha256?: string | null;
  readonly deliveredTo?: string | null;
  readonly requestedAt: string;
}

const EXPORT_FORMATS = ['JSON', 'CSV', 'PDF', 'XBRL'] as const;

function statusColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'READY') return 'var(--color-success)';
  if (s === 'DELIVERED') return 'var(--color-text-link)';
  if (s === 'GENERATING') return 'var(--color-warning)';
  if (s === 'CANCELLED') return 'var(--color-danger)';
  return 'var(--color-text-muted)';
}

export function RegulatorExportPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RegulatorExportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [regulatorName, setRegulatorName] = useState('');
  const [inquiryReference, setInquiryReference] = useState('');
  const [scope, setScope] = useState('');
  const [exportFormat, setExportFormat] = useState<string>('JSON');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    apiRequest<{ data: RegulatorExportRow[] }>('/v1/audit/regulator-exports', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!regulatorName.trim() || !scope.trim()) {
      setError(t('m21.regulatorExport.validationRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest('/v1/audit/regulator-exports', {
        method: 'POST',
        token,
        body: {
          regulatorName: regulatorName.trim(),
          inquiryReference: inquiryReference.trim() || undefined,
          scope: scope.trim(),
          exportFormat,
          periodStart: periodStart ? new Date(periodStart).toISOString() : undefined,
          periodEnd: periodEnd ? new Date(periodEnd).toISOString() : undefined,
        },
      });
      setRegulatorName(''); setInquiryReference(''); setScope('');
      setExportFormat('JSON'); setPeriodStart(''); setPeriodEnd('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (id: string, action: 'generate' | 'deliver' | 'cancel') => {
    setError(null);
    setBusyId(id);
    try {
      let body: Record<string, unknown> | undefined;
      if (action === 'deliver') {
        const to = window.prompt(t('m21.regulatorExport.deliverPrompt'));
        if (!to || !to.trim()) { setBusyId(null); return; }
        body = { deliveredTo: to.trim() };
      } else if (action === 'cancel') {
        const reason = window.prompt(t('m21.regulatorExport.cancelPrompt'));
        if (!reason || !reason.trim()) { setBusyId(null); return; }
        body = { reason: reason.trim() };
      }
      await apiRequest(`/v1/audit/regulator-exports/${id}/${action}`, { method: 'POST', token, body });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const inputStyle = { padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' } as const;

  return (
    <main aria-label="regulator-export">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m21.regulatorExport.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m21.regulatorExport.subtitle')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form aria-label="regulator-export-form" onSubmit={submit}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 24, maxWidth: 1000 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 200px' }}>
          {t('m21.regulatorExport.formRegulator')}
          <input aria-label="regulator-name" value={regulatorName} onChange={(e) => setRegulatorName(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 160px' }}>
          {t('m21.regulatorExport.formInquiry')}
          <input aria-label="inquiry-reference" value={inquiryReference} onChange={(e) => setInquiryReference(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 240px' }}>
          {t('m21.regulatorExport.formScope')}
          <input aria-label="scope" value={scope} onChange={(e) => setScope(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m21.regulatorExport.formFormat')}
          <select aria-label="export-format" value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} style={inputStyle}>
            {EXPORT_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m21.regulatorExport.formPeriodStart')}
          <DateInput aria-label="period-start" type="datetime-local" value={periodStart} onValueChange={(nextValue) => setPeriodStart(nextValue)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m21.regulatorExport.formPeriodEnd')}
          <DateInput aria-label="period-end" type="datetime-local" value={periodEnd} onValueChange={(nextValue) => setPeriodEnd(nextValue)} style={inputStyle} />
        </label>
        <button type="submit" data-testid="submit-export" disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid var(--color-brand-primary)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)' }}>
          {submitting ? t('m21.regulatorExport.requesting') : t('m21.regulatorExport.requestButton')}
        </button>
      </form>

      {!rows ? (
        <p>{t('m21.regulatorExport.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="exports-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m21.regulatorExport.empty')}</p>
      ) : (
        <table aria-label="regulator-exports-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border-default)' }}>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colRegulator')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colInquiry')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colFormat')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colStatus')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colRecords')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colChecksum')}</th>
              <th style={{ padding: 8 }}>{t('m21.regulatorExport.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const status = x.status.toUpperCase();
              const disabled = busyId === x.regulatorExportId;
              return (
                <tr key={x.regulatorExportId} data-testid={`export-row-${x.regulatorExportId}`}
                    style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                  <td style={{ padding: 8 }}>{x.regulatorName}</td>
                  <td style={{ padding: 8 }}>{x.inquiryReference ?? '—'}</td>
                  <td style={{ padding: 8 }}>{x.exportFormat}</td>
                  <td style={{ padding: 8, color: statusColor(x.status), fontWeight: 600 }}>{x.status}</td>
                  <td style={{ padding: 8 }}>{x.recordCount ?? '—'}</td>
                  <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>
                    {x.checksumSha256 ? `${x.checksumSha256.slice(0, 12)}…` : '—'}
                  </td>
                  <td style={{ padding: 8, display: 'flex', gap: 6 }}>
                    {status === 'REQUESTED' && (
                      <>
                        <button data-testid={`generate-${x.regulatorExportId}`} disabled={disabled}
                                onClick={() => act(x.regulatorExportId, 'generate')}
                                style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-success)', background: 'var(--color-success)', color: 'var(--color-text-inverse)' }}>{t('m21.regulatorExport.generate')}</button>
                        <button data-testid={`cancel-${x.regulatorExportId}`} disabled={disabled}
                                onClick={() => act(x.regulatorExportId, 'cancel')}
                                style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-danger)', background: 'var(--color-bg-surface)', color: 'var(--color-danger)' }}>{t('m21.regulatorExport.cancel')}</button>
                      </>
                    )}
                    {status === 'READY' && (
                      <button data-testid={`deliver-${x.regulatorExportId}`} disabled={disabled}
                              onClick={() => act(x.regulatorExportId, 'deliver')}
                              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-text-link)', background: 'var(--color-text-link)', color: 'var(--color-text-inverse)' }}>{t('m21.regulatorExport.deliver')}</button>
                    )}
                    {(status === 'DELIVERED' || status === 'CANCELLED') && (
                      <span style={{ color: 'var(--color-text-muted)' }}>
                        {status === 'DELIVERED' && x.deliveredTo ? `→ ${x.deliveredTo}` : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
